<template>
    <!-- * 蛋白对照表 -->
    <module-codon-contrast ref="contrast" />
    <!-- * 突变模型 -->
    <div :class="css.container">
        <div :class="css.header" class="flex items-center justify-between">
            <div class="flex">
                <h2 class="font-bold text-[16px] text-emphas leading-[32px]">
                    {{ $t("MutationsInputsPlaceholder") }}
                </h2>
                <div
                    class="transition duration-200 bg-color4 cursor-pointer leading-[32px] px-[8px] ml-[16px] rounded-[2px] hover:bg-main hover:text-white"
                    @click="(event) => contrast?.open(event.target as HTMLDivElement)">
                    {{ $t("CodonChart") }}
                </div>
            </div>
            <div class="flex items-center" v-if="explain">
                <h2 class="font-normal transition duration-200 cursor-pointer underline underline-offset-2 hover:text-main" @click="$emit('info')">
                    {{ $t("ModelIntroduction") }}
                </h2>
                <div class="w-[1px] h-[12px] mx-[16px] bg-emphas"></div>
                <h2 class="font-normal transition duration-200 cursor-pointer underline underline-offset-2 hover:text-main" @click="$emit('video')">
                    {{ $t("VideoTutorial") }}
                </h2>
                <div class="w-[1px] h-[12px] mx-[16px] bg-emphas"></div>
                <h2 class="font-normal transition duration-200 cursor-pointer underline underline-offset-2 hover:text-main" @click="$emit('image')">
                    {{ $t("TextTutorial") }}
                </h2>
            </div>
        </div>

        <div :class="css.input">
            <div
                v-for="(res, key) in data"
                :key="res.key"
                class="flex items-start bg-color7 mt-[16px] px-[24px] pt-[12px] pb-[16px] max-lg:px-[20px] max-lg:pt-[4px] max-lg:pb-[12px]">
                <!-- * 序号 -->
                <div
                    class="shrink-0 font-bold text-[20px] text-emphas leading-[48px] max-xl:leading-[40px] max-lg:leading-[36px] mt-[34px] max-lg:mt-[30px]">
                    {{ (key + 1).toString().padStart(2, "0") }}
                </div>
                <!-- * 表单 -->
                <ui-form class="flex-1 ml-[32px] mr-[24px] max-lg:ml-[24px] max-lg:mr-[16px]" :data="res" :rules="rules" ref="forms">
                    <!-- * 表单内容（粘贴） -->
                    <div class="flex items-start">
                        <ui-form-item :class="css.module" class="w-[160px] max-lg:w-[136px]">
                            <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                <h2 class="font-normal">{{ useColon($t("InputMode")) }}</h2>
                                <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                    <div>{{ $t("PredictionSpecification1") }}</div>
                                    <div>{{ $t("PredictionSpecification2") }}</div>
                                    <div>{{ $t("PredictionSpecification3") }}</div>
                                </helper-modal>
                            </div>
                            <ui-select v-model="res.mode" width="100%" :candidates="vars.modes" @change="methods.mode(key)" />
                        </ui-form-item>

                        <template v-if="res.mode != 'Paste'">
                            <ui-form-item :class="css.module" class="w-[160px] max-lg:w-[136px]" prop="name">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("GeneName")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        {{ $t("GeneSpecification") }}
                                    </helper-modal>
                                </div>
                                <default-search
                                    v-model="res.name"
                                    width="100%"
                                    name="name"
                                    mode="select"
                                    type="gene"
                                    :query="{ tax_names: species }"
                                    :width-extra="240"
                                    :placeholder="$t('NewGene')"
                                    @search="(r) => methods.gene(key, r)" />
                            </ui-form-item>
                        </template>

                        <template v-if="res.mode == 'Paste'">
                            <ui-form-item :class="css.module" class="w-[256px]" prop="site" ref="sites">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("StandardMutationNomenclature")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        <render-mutation-describe2 />
                                    </helper-modal>
                                </div>
                                <ui-input
                                    v-model="res.data.keyword"
                                    name="site"
                                    width="100%"
                                    placeholder="e.g. TP53:c.123T>A"
                                    @change="methods.analysis(key)" />
                            </ui-form-item>
                        </template>

                        <ui-form-item :class="css.module" class="w-[240px] max-lg:w-[220px]">
                            <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                <h2 class="font-normal">{{ useColon($t("TranscriptID")) }}</h2>
                                <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                    {{ $t("TranscriptIDDescribe") }}
                                </helper-modal>
                            </div>
                            <ui-tooltip-follow class="w-[100%]" :z-index="67" :disabled="!res.transcript">
                                <template #content>{{ res.transcripts.find((v) => v.value == res.transcript)?.name }}</template>
                                <ui-select
                                    v-model="res.transcript"
                                    width="100%"
                                    :disabled="!res.transcripts.length"
                                    :candidates="res.transcripts"
                                    @change="methods.transcript(key)">
                                    <template #candidate="{ data }">{{ data.name }}</template>
                                </ui-select>
                            </ui-tooltip-follow>
                        </ui-form-item>

                        <template v-if="res.mode == 'Variant'">
                            <ui-form-item :class="css.module" class="w-[256px]" prop="site" ref="sites">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("StandardMutationNomenclature")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        <render-mutation-describe />
                                    </helper-modal>
                                </div>
                                <ui-input
                                    v-model="res.data.keyword"
                                    name="site"
                                    width="100%"
                                    placeholder="e.g. c.123G>A"
                                    :disabled="!res.id"
                                    @change="methods.analysis(key)" />
                            </ui-form-item>
                        </template>

                        <template v-if="res.mode == 'Protein'">
                            <ui-form-item :class="css.module" class="w-[256px]" prop="site" ref="sites">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("ProteinCoordinates")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        {{ $t("TranscriptCoordinatesDescribe") }}
                                    </helper-modal>
                                </div>
                                <ui-input
                                    v-model="res.data.site"
                                    type="number"
                                    name="site"
                                    width="100%"
                                    :placeholder="$t('Placeholder.ProteinSite')"
                                    :disabled="!res.id"
                                    @input="methods.protein_site(key)" />
                            </ui-form-item>
                        </template>

                        <template v-if="res.mode == 'Transcript'">
                            <ui-form-item :class="css.module" class="w-[296px]" prop="site" ref="sites">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("TranscriptCoordinates")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        {{ $t("TranscriptCoordinatesDescribe") }}
                                    </helper-modal>
                                </div>
                                <ui-steps-input
                                    :class="css.steps"
                                    v-model="res.data.coordinate"
                                    type="number"
                                    name="site"
                                    :width="296"
                                    :spacing="6"
                                    :width-number="48"
                                    :disabled="!res.id"
                                    :placeholder="{ start: $t('CdsStart'), end: $t('CdsEnd') }"
                                    @change="methods.transcript_site(key)" />
                            </ui-form-item>
                        </template>

                        <template v-if="res.mode == 'Protein' || ((res.mode == 'Paste' || res.mode == 'Variant') && res.data.mode == 'codon')">
                            <ui-form-item :class="css.module" class="w-[140px]">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("ReferenceBase")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        {{ $t("ReferenceBaseDescribe") }}
                                    </helper-modal>
                                </div>
                                <div class="ui-input ui-readonly-status">
                                    <input class="ui-form-control" type="text" :value="utils.reference(res)" readonly />
                                </div>
                            </ui-form-item>

                            <ui-form-item :class="css.module" class="w-[160px] max-lg:w-[140px]">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("AlterationMutant")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        {{ $t("AlterationBaseDescribe2") }}
                                    </helper-modal>
                                </div>
                                <module-codon-select
                                    v-model="res.alt"
                                    :disabled="!res.id"
                                    :matchs="res.data.codons"
                                    :contrast="res.ref"
                                    @change="methods.alt(key)" />
                            </ui-form-item>
                        </template>

                        <template v-else>
                            <ui-form-item :class="css.module" class="w-[112px]">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("ReferenceBase")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        {{ $t("ReferenceBaseDescribe") }}
                                    </helper-modal>
                                </div>
                                <ui-input v-model="res.ref" width="100%" :readonly="true" />
                            </ui-form-item>

                            <ui-form-item :class="css.module" class="w-[112px]">
                                <div class="flex items-center mb-[4px] max-lg:mb-[0]">
                                    <h2 class="font-normal">{{ useColon($t("AlterationMutant")) }}</h2>
                                    <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                        <div>{{ $t("TranscriptAlterationBaseDescribe1") }}</div>
                                        <div>{{ $t("TranscriptAlterationBaseDescribe2") }}</div>
                                    </helper-modal>
                                </div>
                                <ui-input v-model="res.alt" width="100%" :disabled="!res.id" @input="methods.alt(key)" />
                            </ui-form-item>
                        </template>

                        <div
                            v-show="res.id"
                            class="flex flex-1 justify-end select-none font-bold text-[16px] max-lg:text-[14px] leading-[48px] max-xl:leading-[40px] max-lg:leading-[36px] mt-[34px] max-lg:mt-[30px]">
                            <div
                                class="flex items-center justify-end transition duration-200 text-emphas cursor-pointer hover:text-main"
                                @click="methods.visible(key)">
                                <div class="underline underline-offset-4">{{ res.visible ? $t("FoldDiagram") : $t("UnfoldDiagram") }}</div>
                                <ui-icon
                                    name="arrow"
                                    class="transition duration-200 ml-[4px]"
                                    :style="res.visible ? 'transform: rotate(180deg)' : ''" />
                            </div>
                        </div>
                    </div>

                    <!-- * 表单预览图 -->
                    <module-preview ref="views" v-show="res.id && res.visible" />
                </ui-form>

                <!-- * 控制器 -->
                <div class="shrink-0 w-[32px] h-[32px] mt-[42px] max-xl:mt-[38px] max-lg:mt-[32px]">
                    <div
                        class="transition duration-200 h-[32px] flex items-center justify-center cursor-pointer bg-emphas/50 rounded-[50%] hover:bg-main"
                        v-if="data.length > 1"
                        @click="methods.remove(key)">
                        <ui-icon name="close2" class="text-[16px] text-white" />
                    </div>
                </div>
            </div>

            <ui-button class="mt-[16px]" :width="108" :disabled="data.length >= 5" @click="methods.append">
                <ui-icon name="plus" class="text-[12px]" />
                <span class="ml-[6px]">{{ $t("AddAMutant") }}</span>
            </ui-button>
        </div>

        <div :class="css.footer">
            <div :class="css.checkboxs" class="flex items-center select-none" v-if="define.mode != 'RNA Splicer'">
                <!-- * 复选按钮 -->
                <ui-checkbox :class="[styles.checkbox, css.checkbox]" :value="$t('RNASplicingPredictionSasAlsoPerformed')" v-model="sync" />
                <!-- * 悬浮提示 -->
                <helper-modal :class="[css.helper]" :width="300" :size="16" :icon="10" theme="grey">
                    {{ $t("RNASplicingPredictionSpecification") }}
                </helper-modal>
            </div>

            <div class="text-center" :class="define.mode == 'RNA Splicer' && 'mt-[32px]'">
                <ui-button size="middle" @click="methods.submit" :width="144"><h2>{{ $t("Submit") }}</h2></ui-button>
            </div>
        </div>
    </div>
</template>

<script lang="tsx" setup>
//* 组件库
import { UiIcon, UiForm, UiInput, UiSelect, UiButton, UiCheckbox, UiFormItem, UiStepsInput, UiTooltipFollow } from "various-ui";
//* 公共组件
import { DefaultSearch } from "@/components/controls";
import { HelperModal } from "@/components/windows";
//* 私有组件
import ModuleCodonContrast from "../codon-contrast/index.vue";
import ModuleCodonSelect from "../codon-select/index.vue";
import ModulePreview from "./components/preview/index.vue";
//* 组件属性
import { ModulePropsOption, ModuleEmits } from "./src/types";
import { useComposable } from "./src/composable";
import { utils } from "./src/utils";
//* 样式
import css from "./index.module.less";
import styles from "@/assets/style/src/index.module.less";

//* 插件
const i18n = useI18n();
//* 组件属性注册
const define = defineProps(ModulePropsOption);
const emits = defineEmits(ModuleEmits);

//* 组合函数
const {
    refs: { sync },
    nodes: { forms, sites, views, contrast },
    vars,
    data,
    rules,
    methods,
} = useComposable(define, emits);

//* 渲染函数（突变模板格式说明）
const renderMutationDescribe = defineComponent({
    render() {
        return (
            <div>
                <div>{i18n.t("FastExpressDescribe1")}</div>
                <div>（1）c.123G&gt;A</div>
                <div>（2）c.123_124TG&gt;C</div>
                <div>（3）c.169+2T&gt;C</div>
                <div>（4）c.45_46insGC</div>
                <div>（5）c.36_38del</div>
                <div>（6）c.45_47delinsTT</div>
                <div>（7）c.-1G&gt;T</div>
                <div>（8）c.*16G&gt;T</div>
                <div>（9）c.23+4_23+5GG&gt;CT</div>
                <div>（10）p.Ser3435Ter</div>
                <div style="margin-top: 10px">{i18n.t("FastExpressDescribe2")}</div>
            </div>
        );
    },
});

//* 渲染函数（突变模板格式说明）
const renderMutationDescribe2 = defineComponent({
    render() {
        return (
            <div>
                <div>{i18n.t("FastExpressDescribe1")}</div>
                <div>（1）TP53: c.123T&gt;A</div>
                <div>（2）TP53: c.123_124TG&gt;C</div>
                <div>（3）TP53: c.169+2T&gt;C</div>
                <div>（4）TP53: c.45_46insGC</div>
                <div>（5）COL4A5: c.36_38del</div>
                <div>（6）COL4A5: c.45_47delinsTT</div>
                <div>（7）COL4A5: c.-1G&gt;T</div>
                <div>（8）DMD: c.*16G&gt;T</div>
                <div>（9）DMD: c.23+4_23+5GG&gt;CT</div>
                <div>（10）DMD: p.Ser3435Ter</div>
                <div style="margin-top: 10px">{i18n.t("FastExpressDescribe2")}</div>
            </div>
        );
    },
});

//* 组件属性导出
defineExpose({ init: methods.init, submit: methods.submit });
//* 页面挂载时，默认渲染且展示可视化图形
onMounted(() => {
    data.forEach((value, key) => {
        if (value.id) {
            value.visible = true;
            views.value[key]?.render(value);
        }
    });
});
</script>
